/**
 * Minimale, dependency-vrije "beacon"-route naar Application Insights, voor de
 * plekken waar de volledige SDK niet (meer) beschikbaar is: global-error (bewust
 * dependency-light) en het moment vlak vóór een window.location.reload().
 *
 * De ClientTelemetryProvider onthoudt bij init de connection-string-onderdelen
 * in sessionStorage (rememberTelemetryBeaconConfig). verstuurTelemetryBeacon
 * bouwt daarmee één EventData-envelope en verstuurt die met navigator.sendBeacon
 * (overleeft een page-unload/reload; geen CORS-preflight door de text/plain-body,
 * hetzelfde transport dat de App Insights SDK zelf als unload-fallback gebruikt).
 *
 * LET OP - global-error.tsx (portal) bevat een geïnlinede kopie van het
 * lees+verstuur-deel (het mag niet van de @streep/ui-bundle afhangen: als juist
 * die chunk faalt te laden moet de foutpagina nog renderen). Wijzig je hier de
 * storage-key of het envelope-formaat, werk die kopie dan mee bij.
 *
 * PII: stuur uitsluitend gestructureerde, opake properties mee (digest,
 * error-naam, vlaggen). Nooit vrije foutmeldingen of URL's/paden - deze route
 * loopt bewust NIET door redactTelemetryItem. Enige toegestane uitzondering:
 * het via chunkPadUitFout (use-reload-on-chunk-error.ts) strikt geëxtraheerde
 * /_next/static/...-chunk-pad (geen host/query/vrije tekst, lengte-geklemd).
 * De envelope draagt daarnaast de opake gebruikers-id als `ai.user.authUserId`
 * (zie rememberTelemetryBeaconUser) - nooit naam/e-mail; expliciete keuze voor
 * herleidbaarheid (Burak, 2026-08-13), zelfde id als de SDK-route. KQL-joins
 * op UserAuthenticatedId altijd samen met AppRoleName filteren: de klant-app
 * gebruikt client-side de kale ClientPortalAccount-id (zonder de server-side
 * `cpa:`-prefix - die zou de klem hier ook niet passeren).
 */

export const TELEMETRY_BEACON_STORAGE_KEY = 'streep:ai-beacon'
export const TELEMETRY_BEACON_USER_KEY = 'streep:ai-beacon-user'

/**
 * Onthoudt (of wist, bij null/ongeldig) de OPAKE gebruikers-id voor de
 * beacon-route, zodat ook global-error-events herleidbaar zijn naar een
 * gebruiker - dezelfde id (User.id-cuid, nooit naam/e-mail) die de SDK-route
 * via setAuthenticatedUserContext meegeeft. Blijft bewust staan tot een andere
 * gebruiker inlogt of de tab sluit (sessionStorage): een fout vlak ná
 * uitloggen is dan nog herleidbaar tot de laatst ingelogde gebruiker.
 */
export function rememberTelemetryBeaconUser(userId: string | null): void {
  if (typeof window === 'undefined') return
  try {
    if (userId && /^[\w-]{1,64}$/.test(userId)) {
      window.sessionStorage.setItem(TELEMETRY_BEACON_USER_KEY, userId)
    } else {
      // Ongeldige vorm = bewust wissen, niet normaliseren: liever geen
      // attributie dan een gemangelde of onbetrouwbare id in de telemetrie.
      window.sessionStorage.removeItem(TELEMETRY_BEACON_USER_KEY)
    }
  } catch {
    // sessionStorage geblokkeerd - beacon is best-effort.
  }
}

export interface TelemetryBeaconConfig {
  iKey: string
  ingestionEndpoint: string
  role?: string
  appVersion?: string
}

/**
 * Parset de App Insights connection-string naar iKey + ingestion-endpoint.
 * Sleutels case-insensitief, net als de officiële SDK - anders staat de beacon
 * stil uit terwijl de SDK gewoon werkt zodra de Key Vault-waarde in casing
 * afwijkt.
 */
export function parseConnectionString(
  connectionString: string,
): Pick<TelemetryBeaconConfig, 'iKey' | 'ingestionEndpoint'> | null {
  if (!connectionString) return null
  const parts = new Map<string, string>()
  for (const segment of connectionString.split(';')) {
    const i = segment.indexOf('=')
    if (i <= 0) continue
    parts.set(segment.slice(0, i).trim().toLowerCase(), segment.slice(i + 1).trim())
  }
  const iKey = parts.get('instrumentationkey')
  if (!iKey) return null
  const endpoint = parts.get('ingestionendpoint') ?? 'https://dc.services.visualstudio.com'
  return { iKey, ingestionEndpoint: endpoint.replace(/\/+$/, '') }
}

/**
 * Onthoudt de beacon-config in sessionStorage. Aangeroepen door de
 * ClientTelemetryProvider vóór de (lui-geladen) SDK-import, zodat de config er
 * óók is wanneer juist die import faalt (het iOS-resume-scenario).
 */
export function rememberTelemetryBeaconConfig(
  connectionString: string | undefined,
  cloudRole?: string,
  appVersion?: string,
): void {
  if (!connectionString || typeof window === 'undefined') return
  try {
    const parsed = parseConnectionString(connectionString)
    if (!parsed) return
    const config: TelemetryBeaconConfig = {
      ...parsed,
      ...(cloudRole ? { role: cloudRole } : {}),
      ...(appVersion ? { appVersion } : {}),
    }
    window.sessionStorage.setItem(TELEMETRY_BEACON_STORAGE_KEY, JSON.stringify(config))
  } catch {
    // sessionStorage geblokkeerd (private mode) - beacon is best-effort.
  }
}

/**
 * Verstuurt één custom event rechtstreeks naar de App Insights-ingest via
 * navigator.sendBeacon. Best-effort: retourneert false wanneer er geen config,
 * geen sendBeacon of een storage-fout is. Properties moeten opaak zijn (geen
 * PII, geen vrije foutmeldingen) - zie de module-doc.
 */
export function verstuurTelemetryBeacon(
  eventName: string,
  properties?: Record<string, string>,
): boolean {
  try {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
    if (typeof navigator.sendBeacon !== 'function') return false
    const raw = window.sessionStorage.getItem(TELEMETRY_BEACON_STORAGE_KEY)
    if (!raw) return false
    const config = JSON.parse(raw) as TelemetryBeaconConfig
    if (!config.iKey || !config.ingestionEndpoint) return false

    // Opake gebruikers-id (indien ingelogd) -> UserAuthenticatedId-kolom,
    // zelfde kolom als de SDK-events. Geklemd op een simpele identifier.
    const rawUser = window.sessionStorage.getItem(TELEMETRY_BEACON_USER_KEY)
    const authUserId = rawUser && /^[\w-]{1,64}$/.test(rawUser) ? rawUser : undefined

    const envelope = {
      name: `Microsoft.ApplicationInsights.${config.iKey.replace(/-/g, '')}.Event`,
      time: new Date().toISOString(),
      iKey: config.iKey,
      tags: {
        ...(config.role ? { 'ai.cloud.role': config.role } : {}),
        // Zelfde ClientType ('Browser') en app-versie als de SDK-events, zodat
        // beacon-events niet uit ClientType-gefilterde queries vallen en stale
        // build vs. huidige build uit de data zelf af te leiden is.
        'ai.device.type': 'Browser',
        ...(config.appVersion ? { 'ai.application.ver': config.appVersion } : {}),
        ...(authUserId ? { 'ai.user.authUserId': authUserId } : {}),
        'ai.internal.sdkVersion': 'streep-beacon:1',
      },
      data: {
        baseType: 'EventData',
        baseData: { ver: 2, name: eventName, properties: properties ?? {} },
      },
    }
    return navigator.sendBeacon(`${config.ingestionEndpoint}/v2/track`, JSON.stringify([envelope]))
  } catch {
    return false
  }
}
